/**
 * BibleProgress - Comprehensive progress tracking with gamification
 * Features: Streak, % completion, calendar view, badges, milestones
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';

// Icons
import {
  Flame, Trophy, Calendar, BookOpen, Target, Star, Award,
  CheckCircle2, TrendingUp, Crown, Zap, Heart, BookMarked
} from 'lucide-react';

// ============== Stats Overview ==============
const StatsOverview = ({ stats }) => {
  const statItems = [
    {
      icon: Flame,
      label: 'Current Streak',
      value: stats?.current_streak || 0,
      suffix: 'days',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
    },
    {
      icon: Trophy,
      label: 'Longest Streak',
      value: stats?.longest_streak || 0,
      suffix: 'days',
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-amber-50',
    },
    {
      icon: Calendar,
      label: 'Total Days Read',
      value: stats?.total_days_read || 0,
      suffix: 'days',
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: BookMarked,
      label: 'Plans Completed',
      value: stats?.completed_plans || 0,
      suffix: '',
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {statItems.map((item) => (
        <Card key={item.label} className={cn("overflow-hidden", item.bgColor)}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg bg-gradient-to-br",
                item.color
              )}>
                <item.icon className="h-6 w-6" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {item.value}
                  {item.suffix && <span className="text-sm font-normal text-slate-500 ml-1">{item.suffix}</span>}
                </div>
                <div className="text-xs text-slate-600">{item.label}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== Bible Completion Progress ==============
const BibleCompletionCard = ({ stats }) => {
  // For now, we'll calculate based on notes/highlights as a proxy
  // In a real implementation, we'd track chapters read
  const estimatedProgress = Math.min(
    ((stats?.total_days_read || 0) / 365) * 100, // Rough estimate
    100
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-indigo-600" />
          Bible Reading Progress
        </CardTitle>
        <CardDescription>Track your journey through the Bible</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Overall Progress</span>
            <span className="text-sm font-medium text-slate-900">{estimatedProgress.toFixed(1)}%</span>
          </div>
          <Progress value={estimatedProgress} className="h-3" />
        </div>

        {/* Testament Breakdown */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="text-sm text-slate-500 mb-1">Old Testament</div>
            <div className="flex items-center gap-2">
              <Progress value={Math.min(estimatedProgress * 0.6, 100)} className="h-2 flex-1" />
              <span className="text-xs font-medium">{(estimatedProgress * 0.6).toFixed(0)}%</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">929 chapters</div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="text-sm text-slate-500 mb-1">New Testament</div>
            <div className="flex items-center gap-2">
              <Progress value={Math.min(estimatedProgress * 1.2, 100)} className="h-2 flex-1" />
              <span className="text-xs font-medium">{Math.min(estimatedProgress * 1.2, 100).toFixed(0)}%</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">260 chapters</div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center">
            <div className="text-xl font-bold text-indigo-600">{stats?.notes_count || 0}</div>
            <div className="text-xs text-slate-500">Notes</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-600">{stats?.highlights_count || 0}</div>
            <div className="text-xs text-slate-500">Highlights</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-amber-600">{stats?.bookmarks_count || 0}</div>
            <div className="text-xs text-slate-500">Bookmarks</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Reading Calendar ==============
const ReadingCalendar = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // Get days in month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  
  // Mock reading days - in real implementation, this would come from API
  const readDays = new Set([1, 2, 3, 5, 6, 7, 8, 12, 13, 14, 15, 16, 19, 20, 21, today.getDate()]);
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-indigo-600" />
          {monthNames[currentMonth]} {currentYear}
        </CardTitle>
        <CardDescription>Your reading history this month</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before the first of the month */}
          {Array.from({ length: firstDayOfMonth }, (_, i) => (
            <div key={`empty-${i}`} className="h-8" />
          ))}
          
          {/* Day cells */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const isToday = day === today.getDate();
            const isRead = readDays.has(day);
            const isPast = day < today.getDate();
            
            return (
              <div
                key={day}
                className={cn(
                  "h-8 rounded-full flex items-center justify-center text-sm",
                  isToday && "ring-2 ring-indigo-500 ring-offset-1",
                  isRead && "bg-emerald-500 text-white",
                  isPast && !isRead && "bg-slate-100 text-slate-400",
                  !isPast && !isRead && !isToday && "text-slate-600"
                )}
              >
                {isRead ? <CheckCircle2 className="h-4 w-4" /> : day}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-emerald-500" />
            <span className="text-slate-600">Read</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-slate-100" />
            <span className="text-slate-600">Missed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full ring-2 ring-indigo-500" />
            <span className="text-slate-600">Today</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Badges & Achievements ==============
const BadgesCard = ({ stats }) => {
  const badges = [
    {
      id: 'first-read',
      name: 'First Steps',
      description: 'Complete your first day of reading',
      icon: Star,
      color: 'from-blue-500 to-indigo-500',
      earned: (stats?.total_days_read || 0) >= 1,
    },
    {
      id: 'week-streak',
      name: 'Week Warrior',
      description: '7-day reading streak',
      icon: Flame,
      color: 'from-orange-500 to-red-500',
      earned: (stats?.longest_streak || 0) >= 7,
    },
    {
      id: 'month-streak',
      name: 'Monthly Master',
      description: '30-day reading streak',
      icon: Crown,
      color: 'from-amber-500 to-yellow-500',
      earned: (stats?.longest_streak || 0) >= 30,
    },
    {
      id: 'note-taker',
      name: 'Thoughtful Scholar',
      description: 'Write 10 notes',
      icon: BookOpen,
      color: 'from-emerald-500 to-teal-500',
      earned: (stats?.notes_count || 0) >= 10,
    },
    {
      id: 'highlighter',
      name: 'Word Illuminator',
      description: 'Highlight 25 verses',
      icon: Zap,
      color: 'from-purple-500 to-pink-500',
      earned: (stats?.highlights_count || 0) >= 25,
    },
    {
      id: 'plan-completer',
      name: 'Plan Champion',
      description: 'Complete a reading plan',
      icon: Trophy,
      color: 'from-indigo-500 to-violet-500',
      earned: (stats?.completed_plans || 0) >= 1,
    },
  ];

  const earnedCount = badges.filter(b => b.earned).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-indigo-600" />
          Achievements
        </CardTitle>
        <CardDescription>{earnedCount} of {badges.length} badges earned</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className={cn(
                "relative p-4 rounded-xl text-center transition-all",
                badge.earned 
                  ? "bg-white shadow-md" 
                  : "bg-slate-50 opacity-50 grayscale"
              )}
            >
              <div className={cn(
                "w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center text-white shadow-lg bg-gradient-to-br",
                badge.earned ? badge.color : "from-slate-400 to-slate-500"
              )}>
                <badge.icon className="h-6 w-6" />
              </div>
              <h4 className="font-medium text-sm text-slate-900">{badge.name}</h4>
              <p className="text-xs text-slate-500 mt-1">{badge.description}</p>
              
              {badge.earned && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Main Component ==============
const BibleProgress = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['bible-stats'],
    queryFn: () => api.get('/bible/my-stats').then(r => r.data),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <StatsOverview stats={stats} />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Bible Completion */}
        <BibleCompletionCard stats={stats} />
        
        {/* Reading Calendar */}
        <ReadingCalendar />
      </div>

      {/* Badges */}
      <BadgesCard stats={stats} />
    </div>
  );
};

export default BibleProgress;
